import React, { useEffect, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useWindowSize } from '@gloat/hooks';
import { colors } from '../../../../style';
import Panel from '../../../panel';
import { BoxProps, Div, Flex } from '../../../box';
import Icon, { IconProps } from '../../../icon';
import { TOP_NAVBAR_HEIGHT } from '../../top-navbar';
import { RoundButton } from '../../../button';
import { SidebarProvider } from '../sidebar-context';

const SIDEBAR_WIDTH = 298;
const SIDEBAR_COLLAPSED_WIDTH = 56;
const MOBILE_BREAKPOINT = 480;

const AnimatedPanel = animated(Panel);

export type SidebarSingleItem = { type: 'item'; icon: IconProps['name']; title: string; path?: string };
export type SidebarGroupItem = { type: 'group'; icon: IconProps['name']; title: string; items: SidebarSingleItem[] };
export type SidebarItem = SidebarSingleItem | SidebarGroupItem;

type SidebarProps = {
  children?: React.ReactNode;
};

function Sidebar({ children }: SidebarProps) {
  const [hovered, setHovered] = useState(false);
  const [activePath, setActivePath] = useState<string>(window.location.pathname);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);

  const { width: windowWidth } = useWindowSize();

  const isMobileView = Number(windowWidth) < MOBILE_BREAKPOINT;

  const mode = hovered ? 'expanded' : 'collapsed';

  const isExpanded = mode === 'expanded';

  const springProps = useSpring({
    width: isExpanded ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
    config: { tension: 200, friction: 30, duration: 120 },
    onStart: () => {
      if (!isExpanded) {
        setIsAnimationComplete(false);
      }
    },
    onRest: () => {
      if (isExpanded) {
        setIsAnimationComplete(true);
      }
    },
  });

  const shouldShowContent = !isExpanded || isAnimationComplete;

  const onItemFocus = () => {
    if (!hovered && !isMobileView) {
      setHovered(true);
    }
  };

  const handleSidebarClick = () => {
    if (isMobileView) {
      setHovered(!hovered);
    }
  };

  const handleMouseEnter = () => {
    if (!isMobileView) {
      setHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobileView) {
      setHovered(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (hovered && (event.key === 'Escape' || event.key === 'Backspace')) {
        setHovered(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [hovered]);

  return (
    <AnimatedPanel
      variant={'white'}
      display={'flex'}
      flexDirection={'column'}
      position={'relative'}
      className={'sidebar'}
      style={{
        ...springProps,
        height: `calc(100vh - ${TOP_NAVBAR_HEIGHT}px)`,
        borderRight: `1px solid ${colors.gray300}`,
        position: 'fixed',
        top: TOP_NAVBAR_HEIGHT,
        left: 0,
        zIndex: hovered ? 9999 : 1002,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleSidebarClick}
    >
      <Div zIndex={1000} paddingVertical={'sm'} overflowY={'auto'} width={'100%'} height={`100%`}>
        <SidebarProvider value={{ mode, onItemFocus, isMobileView, activePath, setActivePath }}>
          <Flex
            width={'100%'}
            height={'100%'}
            flexShrink={0}
            flexGrow={1}
            justifyContent={'flex-start'}
            flexDirection={'column'}
            gap={'sm'}
          >
            {isMobileView ? (
              <>
                <Div padding={'xs'}>
                  <RoundButton
                    size={'md'}
                    variant={'secondary-clean'}
                    aria-label={'menu'}
                    onClick={() => {
                      setHovered(true);
                    }}
                  >
                    <Icon name={isExpanded ? 'CHEVRON_LEFT' : 'CHEVRON_RIGHT'} />
                  </RoundButton>
                </Div>
                {shouldShowContent && children}
              </>
            ) : (
              shouldShowContent && children
            )}
          </Flex>
        </SidebarProvider>
      </Div>
    </AnimatedPanel>
  );
}

Sidebar.BottomItems = (props: BoxProps) => <Div marginTop={'auto'} {...props} />;
export { Sidebar };
export { useSidebarContext } from '../sidebar-context';
